/**
 * MIQAnticipationBanner.js
 * 
 * Anticipation UX for Days 25-30 of MIQ calibration
 * 
 * States:
 * - Day 25-26: "X days until your MIQ Baseline unlocks"
 * - Day 27-28: "X days until your Baseline Report" + blurred preview cards
 * - Day 29: "Tomorrow: Baseline Day" + mini checklist
 * - Day 30: BaselineReadyModal opens once + persistent card
 */

import React, { useState, useEffect } from 'react';
import { 
  Sparkles, ChevronRight, Lock, Zap, Calendar, 
  CheckCircle, Circle, Droplets, Utensils, ClipboardList,
  Brain, TrendingUp, Target, Heart
} from 'lucide-react';
import { brand } from '../../styles/brand';
import { track } from '../../utils/analytics';

const { colors, gradients, typography, radius, shadows, spacing } = brand;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// DAY 25-26: COUNTDOWN BANNER
// ═══════════════════════════════════════════════════════════════════════════════
export const CountdownBanner = ({ daysRemaining, onNavigateToMIQ }) => {
  if (daysRemaining < 1 || daysRemaining > 5) return null;

  return (
    <div
      data-testid="miq-countdown-banner"
      onClick={() => {
        track('miq_countdown_banner_clicked', { days_remaining: daysRemaining });
        onNavigateToMIQ?.();
      }}
      style={{
        background: gradients.primary,
        padding: `${spacing.s3} ${spacing.s4}`,
        borderRadius: radius.lg,
        marginBottom: spacing.s3,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: spacing.s3,
        boxShadow: shadows.glow,
        transition: 'transform 0.2s ease'
      }}
    >
      <div style={{
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <span style={{ fontSize: '20px' }}>🧠</span>
      </div>
      
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: '15px',
          fontWeight: '700',
          color: colors.text,
          marginBottom: '2px'
        }}>
          {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} until your MIQ Baseline
        </div>
        <div style={{
          fontSize: '13px',
          color: 'rgba(255, 255, 255, 0.85)'
        }}>
          Keep logging to strengthen your model
        </div>
      </div>
      
      <ChevronRight size={20} color="rgba(255, 255, 255, 0.7)" />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DAY 27-28: COUNTDOWN WITH BLURRED PREVIEW
// ═══════════════════════════════════════════════════════════════════════════════
export const CountdownWithPreview = ({ daysRemaining, previewSections, onNavigateToMIQ }) => {
  if (daysRemaining < 2 || daysRemaining > 3) return null;

  const sectionIcons = {
    'What MIQ Learned About You': Brain,
    'Habit Signals Dashboard': TrendingUp,
    'Nutrition Pattern Analysis': Utensils,
    'Next 30 Days Plan': Target
  };

  return (
    <div
      data-testid="miq-countdown-preview"
      style={{
        background: `linear-gradient(135deg, ${colors.panel2} 0%, rgba(102, 126, 234, 0.15) 100%)`,
        border: `2px solid ${colors.primary}`,
        borderRadius: radius.lg,
        padding: spacing.s4,
        marginBottom: spacing.s3
      }}
    >
      {/* Header */}
      <div 
        onClick={() => {
          track('miq_countdown_preview_clicked', { days_remaining: daysRemaining });
          onNavigateToMIQ?.();
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.s3,
          marginBottom: spacing.s4,
          cursor: 'pointer'
        }}
      >
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: gradients.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: shadows.glow
        }}>
          <Sparkles size={24} color="white" />
        </div>
        
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '16px',
            fontWeight: '700',
            color: colors.text,
            marginBottom: '2px'
          }}>
            {daysRemaining} days until your Baseline Report
          </div>
          <div style={{
            fontSize: '13px',
            color: colors.textSecondary
          }}>
            Your personalized intelligence awaits
          </div>
        </div>
        
        <ChevronRight size={20} color={colors.textSecondary} />
      </div>

      {/* Blurred Preview Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(2, 1fr)', 
        gap: '10px'
      }}>
        {previewSections?.map((section, i) => {
          const Icon = sectionIcons[section] || Brain;
          return (
            <div
              key={i}
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: radius.md,
                padding: '12px',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Icon size={14} color={colors.primary} />
                <span style={{ fontSize: '11px', fontWeight: '600', color: colors.text }}>{section}</span>
              </div>
              {/* Blurred placeholder content */}
              <div style={{
                height: '20px',
                background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)',
                borderRadius: '4px',
                filter: 'blur(4px)'
              }} />
              {/* Lock overlay */}
              <div style={{
                position: 'absolute',
                top: '8px',
                right: '8px'
              }}>
                <Lock size={12} color={colors.textSecondary} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DAY 29: TOMORROW CHECKLIST
// ═══════════════════════════════════════════════════════════════════════════════
export const Day29Checklist = ({ checklist, onNavigateToMIQ, onLogMeal, onCheckIn }) => {
  if (!checklist) return null;

  const items = [
    { key: 'log_meal', icon: Utensils, action: onLogMeal },
    { key: 'daily_checkin', icon: ClipboardList, action: onCheckIn },
    { key: 'hydration', icon: Droplets, action: onCheckIn }
  ];

  return (
    <div
      data-testid="miq-day29-checklist"
      style={{
        background: `linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)`,
        border: `2px solid rgba(255, 215, 0, 0.4)`,
        borderRadius: radius.lg,
        padding: spacing.s4,
        marginBottom: spacing.s3
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.s3,
        marginBottom: spacing.s4
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 15px rgba(255, 193, 7, 0.3)'
        }}>
          <Calendar size={24} color="#1a1a2e" />
        </div>
        
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '16px',
            fontWeight: '700',
            color: colors.text,
            marginBottom: '2px'
          }}>
            Tomorrow: Baseline Day! 🎉
          </div>
          <div style={{
            fontSize: '13px',
            color: colors.textSecondary
          }}>
            Complete these to maximize your report
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {items.map(({ key, icon: Icon, action }) => {
          const item = checklist[key];
          if (!item) return null;
          
          return (
            <div
              key={key}
              onClick={() => !item.done && action?.()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                background: item.done ? 'rgba(56, 239, 125, 0.1)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${item.done ? 'rgba(56, 239, 125, 0.3)' : 'rgba(255,255,255,0.08)'}`,
                borderRadius: radius.md,
                cursor: item.done ? 'default' : 'pointer',
                opacity: item.optional && !item.done ? 0.7 : 1
              }}
            >
              {item.done ? (
                <CheckCircle size={20} color="#38ef7d" />
              ) : (
                <Circle size={20} color={colors.textSecondary} />
              )}
              <Icon size={16} color={item.done ? '#38ef7d' : colors.textSecondary} />
              <span style={{
                flex: 1,
                fontSize: '13px',
                color: item.done ? '#38ef7d' : colors.text,
                textDecoration: item.done ? 'line-through' : 'none'
              }}>
                {item.label}
              </span>
              {item.optional && (
                <span style={{
                  fontSize: '10px',
                  color: colors.textSecondary,
                  padding: '2px 6px',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '10px'
                }}>
                  Optional
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DAY 30: BASELINE READY CARD
// ═══════════════════════════════════════════════════════════════════════════════
export const BaselineReadyCard = ({ onViewReport, isGeniusTier, onUpgrade }) => {
  return (
    <div
      data-testid="miq-baseline-ready-card"
      style={{
        background: `linear-gradient(135deg, ${colors.panel2} 0%, rgba(102, 126, 234, 0.2) 100%)`,
        border: `2px solid ${colors.primary}`,
        borderRadius: radius.lg,
        padding: spacing.s4,
        marginBottom: spacing.s3,
        boxShadow: shadows.glow
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.s3,
        marginBottom: spacing.s4
      }}>
        <div style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: gradients.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: shadows.glow,
          animation: 'pulse 2s ease-in-out infinite'
        }}>
          <Zap size={28} color="white" />
        </div>
        
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '18px',
            fontWeight: '700',
            color: colors.text,
            marginBottom: '4px'
          }}>
            Your MIQ Baseline is Ready! 🎉
          </div>
          <div style={{
            fontSize: '13px',
            color: colors.textSecondary
          }}>
            30 days of learning complete
          </div>
        </div>
      </div>

      <button
        data-testid={isGeniusTier ? "view-baseline-report-btn" : "upgrade-genius-btn"}
        onClick={() => {
          if (isGeniusTier) {
            track('miq_baseline_card_view_clicked');
            onViewReport?.();
          } else {
            track('miq_baseline_card_upgrade_clicked');
            onUpgrade?.();
          }
        }}
        style={{
          width: '100%',
          padding: `${spacing.s3} ${spacing.s4}`,
          background: isGeniusTier ? gradients.primary : 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
          border: 'none',
          borderRadius: radius.md,
          color: isGeniusTier ? 'white' : '#1a1a2e',
          fontSize: '15px',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          boxShadow: isGeniusTier ? shadows.glow : '0 4px 15px rgba(255, 193, 7, 0.3)'
        }}
      >
        {isGeniusTier ? (
          <>
            <Sparkles size={18} />
            View Baseline Report
          </>
        ) : (
          <>
            <Lock size={16} />
            Upgrade to Genius Plaite
          </>
        )}
      </button>

      {!isGeniusTier && (
        <p style={{
          fontSize: '11px',
          color: colors.textSecondary,
          textAlign: 'center',
          marginTop: '10px',
          marginBottom: 0
        }}>
          Unlock your full metabolic intelligence profile
        </p>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// BASELINE READY MODAL (One-time celebration)
// ═══════════════════════════════════════════════════════════════════════════════
export const BaselineReadyModal = ({ isOpen, onClose, onViewReport, isGeniusTier, onUpgrade }) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10001,
      padding: spacing.s4
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        borderRadius: radius.xl,
        padding: spacing.s6,
        maxWidth: '400px',
        width: '100%',
        textAlign: 'center',
        border: '2px solid rgba(102, 126, 234, 0.4)',
        boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3)'
      }}>
        {/* Celebration Animation */}
        <div style={{
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: gradients.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          boxShadow: '0 10px 40px rgba(102, 126, 234, 0.5)',
          animation: 'pulse 2s ease-in-out infinite'
        }}>
          <span style={{ fontSize: '48px' }}>🧠</span>
        </div>

        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: colors.text,
          margin: '0 0 12px 0'
        }}>
          Your MIQ Baseline is Ready!
        </h2>

        <p style={{
          fontSize: '14px',
          color: colors.textSecondary,
          margin: '0 0 24px 0',
          lineHeight: 1.6
        }}>
          MIQ has established your metabolic baseline from 30 days of patterns. 
          Your personalized intelligence report is waiting.
        </p>

        <button
          onClick={() => {
            if (isGeniusTier) {
              track('miq_baseline_modal_view_clicked');
              onViewReport?.();
            } else {
              track('miq_baseline_modal_upgrade_clicked');
              onUpgrade?.();
            }
            onClose?.();
          }}
          style={{
            width: '100%',
            padding: `${spacing.s4} ${spacing.s4}`,
            background: isGeniusTier ? gradients.primary : 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            border: 'none',
            borderRadius: radius.md,
            color: isGeniusTier ? 'white' : '#1a1a2e',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: spacing.s3
          }}
        >
          {isGeniusTier ? '✨ View Baseline Report' : '🔓 Upgrade to Genius Plaite'}
        </button>

        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: colors.textSecondary,
            fontSize: '13px',
            cursor: 'pointer',
            padding: '8px'
          }}
        >
          {isGeniusTier ? 'View Later' : 'Maybe Later'}
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMBINED ANTICIPATION COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const MIQAnticipationBanner = ({
  userId,
  calibrationDay,
  isGeniusTier,
  onNavigateToMIQ,
  onViewBaseline,
  onUpgrade,
  onLogMeal,
  onCheckIn
}) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchStatus();
    }
  }, [userId, calibrationDay]);

  const fetchStatus = async () => {
    // Use XMLHttpRequest to bypass any fetch interceptors (analytics, service
    // workers, devtools wrappers) that may consume the response body before we
    // can read it. Same pattern as authService.js.
    try {
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${BACKEND_URL}/api/miq/baseline/${userId}/status`, true);
        xhr.onload = function () {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              return resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              return reject(new Error('Invalid JSON in MIQ status response'));
            }
          }
          return reject(new Error(`MIQ status request failed: ${xhr.status}`));
        };
        xhr.onerror = function () {
          reject(new Error('MIQ status network error'));
        };
        xhr.send();
      });

      if (data && data.success) {
        setStatus(data);

        // Show modal once when baseline is ready and not viewed
        if (data.state === 'ready' && !data.baseline_viewed) {
          const modalShown = localStorage.getItem(`miq_baseline_modal_shown_${userId}`);
          if (!modalShown) {
            setShowModal(true);
            localStorage.setItem(`miq_baseline_modal_shown_${userId}`, 'true');
          }
        }
      }
    } catch (err) {
      console.error('[MIQAnticipationBanner] Error fetching status:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !status) return null;

  const { calibration_day, days_remaining, state, preview_sections, day_29_checklist } = status;

  // Day 30+: Show baseline ready card
  if (state === 'ready' || state === 'viewed') {
    return (
      <>
        <BaselineReadyCard
          onViewReport={onViewBaseline}
          isGeniusTier={isGeniusTier}
          onUpgrade={onUpgrade}
        />
        <BaselineReadyModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onViewReport={onViewBaseline}
          isGeniusTier={isGeniusTier}
          onUpgrade={onUpgrade}
        />
      </>
    );
  }

  // Day 29: Tomorrow checklist
  if (calibration_day === 29) {
    return (
      <Day29Checklist
        checklist={day_29_checklist}
        onNavigateToMIQ={onNavigateToMIQ}
        onLogMeal={onLogMeal}
        onCheckIn={onCheckIn}
      />
    );
  }

  // Day 27-28: Countdown with preview
  if (days_remaining >= 2 && days_remaining <= 3 && preview_sections) {
    return (
      <CountdownWithPreview
        daysRemaining={days_remaining}
        previewSections={preview_sections}
        onNavigateToMIQ={onNavigateToMIQ}
      />
    );
  }

  // Day 25-26: Simple countdown
  if (days_remaining >= 4 && days_remaining <= 5) {
    return (
      <CountdownBanner
        daysRemaining={days_remaining}
        onNavigateToMIQ={onNavigateToMIQ}
      />
    );
  }

  return null;
};

export default MIQAnticipationBanner;
